<template>
  <div class="login-page">
    <div class="keycloak-login-container">
      <div class="login-card">
        <!-- Logo Section -->
        <div class="logo-section">
          <div class="logo-container">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" class="groove-logo-icon">
              <path d="M19.722 19.852h19.326c-.829 8.975-4.942 14.896-12.34 17.763-7.397 2.866-14.386 1.433-20.966-4.3l6.906-6.626c2.51 1.84 4.869 2.76 7.074 2.76 2.206 0 4.268-.844 6.186-2.532l-6.186-7.065zM0 19.36h9.909c.391-4.854 2.702-7.9 6.933-9.137 4.23-1.236 7.798.038 10.702 3.822l7.73-6.309C28.764.456 21.24-1.686 12.701 1.312 4.814 4.08.581 10.097 0 19.36z" fill="#FD6481" fill-rule="evenodd"/>
            </svg>
          </div>
        </div>

        <div class="login-body">
          <div class="spinner-container">
            <div class="spinner"></div>
          </div>
          <p class="status-text">{{ $t('frontend_dashboard_sso_success_login') }}</p>

          <!-- Clear Cache Button (appears after 3 seconds) -->
          <transition name="fade">
            <div v-if="showClearCacheButton" class="clear-cache-container">
              <div class="stuck-message">
                <svg class="warning-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span>Taking longer than expected?</span>
              </div>
              <button @click="clearSiteData" class="clear-cache-btn">
                <svg class="btn-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="1 4 1 10 7 10"></polyline>
                  <polyline points="23 20 23 14 17 14"></polyline>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                </svg>
                Clear Cache & Retry
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import keycloakAuth from '@/services/keycloakAuth';
import publisher from 'publisher';

export default {
  name: 'Login',
  data() {
    return {
      showClearCacheButton: false,
      clearCacheTimer: null,
    };
  },
  async mounted() {
    // Start timer to show clear cache button after 3 seconds
    this.clearCacheTimer = setTimeout(() => {
      this.showClearCacheButton = true;
    }, 3000);

    // Set language based on location if not set
    const page_lang = localStorage.getItem('lang');
    if (!page_lang) {
      this.$root.$i18n.locale = 'en';
      try {
        const response = await fetch('https://cloudflare.com/cdn-cgi/trace');
        const body = await response.text();
        const country = body
          .split('\n')
          .find((string) => string.includes('loc='))
          .split('=')[1];
        if (country === 'BR') {
          localStorage.setItem('lang', 'pt');
          this.$root.$i18n.locale = 'pt';
        }
      } catch (error) {
        console.error('Failed to detect location:', error);
      }
    } else {
      this.$root.$i18n.locale = localStorage.getItem('lang');
    }

    // Check if already authenticated
    const authenticated = await keycloakAuth.init();

    if (authenticated) {
      // User is already logged in, handle the callback
      await this.handleAuthentication();
    } else {
      // Store the intended redirect path
      const redirect = this.$route.query.redirect || localStorage.getItem('redirect') || '/';
      localStorage.setItem('redirect', redirect);

      // OAuth redirect URIs must not contain the Vue hash-router fragment.
      // Keycloak restores the authenticated app at the Groove origin and the
      // router continues from the redirect saved above.
      keycloakAuth.login({
        redirectUri: `${window.location.origin}/`
      });
    }
  },
  beforeDestroy() {
    // Clear timer if component is destroyed
    if (this.clearCacheTimer) {
      clearTimeout(this.clearCacheTimer);
    }
  },
  methods: {
    reportLoginFailure() {
      const failure = keycloakAuth.loginFailure || {
        title: 'Sign-in could not be completed',
        message: 'Sign-in could not be completed.',
      };
      if (this.$root && typeof this.$root.showLoginFailure === 'function') {
        this.$root.showLoginFailure(failure);
      }
    },
    clearSiteData() {
      try {
        // Clear localStorage
        localStorage.clear();

        // Clear sessionStorage
        sessionStorage.clear();

        // Clear all cookies
        document.cookie.split(';').forEach((cookie) => {
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

          // Clear for current domain
          document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';

          // Clear for root domain
          const domain = window.location.hostname;
          document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + domain;

          // Clear for parent domain (e.g., .groove.cm)
          const parts = domain.split('.');
          if (parts.length > 2) {
            const rootDomain = '.' + parts.slice(-2).join('.');
            document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + rootDomain;
          }
        });

        // Clear IndexedDB if available
        if (window.indexedDB) {
          window.indexedDB.databases().then((dbs) => {
            dbs.forEach((db) => {
              window.indexedDB.deleteDatabase(db.name);
            });
          }).catch((err) => {
            console.warn('Failed to clear IndexedDB:', err);
          });
        }

        // Reload the page after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } catch (error) {
        console.error('Failed to clear site data:', error);
        // Still try to reload even if clearing failed
        window.location.reload();
      }
    },
    async handleAuthentication() {
      try {
        // Load complete user profile with backend data
        const user = await keycloakAuth.loadCompleteUserProfile();

        if (user) {
          // Store user in Vuex
          this.$store.commit('login', user);

          // Set tooltip data if available
          if (typeof Tooltip !== 'undefined') {
            console.log('Setting value in tooltip');
            let userLevel = 0;

            if (user.roles && user.roles.indexOf('groove_future_apps') >= 0) {
              userLevel = 1;
            } else if (user.roles && user.roles.indexOf('groovepages_user') >= 0) {
              userLevel = 2;
            }

            Tooltip.API.updateUserData({
              userId: user.id,
              userLevel,
            });
          }

          // Handle redirect
          let redirect = localStorage.getItem('redirect') || '/';

          const rootDomainRedirects = [
            'https://app.groovefunnels.com/#/',
            'https://app.groovefunnels.com/#',
            'https://app.groovefunnels.com',
            'https://app.groovefunnels.com/',
            'https://app.groove.cm/#/',
            'https://app.groove.cm/#',
            'https://app.groove.cm',
            'https://app.groove.cm/',
          ];

          if (
            !redirect ||
            redirect.includes('/login') ||
            rootDomainRedirects.indexOf(redirect) > -1
          ) {
            redirect = '/';
          }

          // Clear stored redirect
          localStorage.removeItem('redirect');

          // Check if it's an external URL
          let url = false;
          try {
            url = new URL(redirect);
          } catch (_) {}

          if (url && (url.protocol === 'http:' || url.protocol === 'https:')) {
            window.location.replace(redirect);
          } else if (redirect.indexOf('builder') !== -1) {
            window.location.replace(redirect);
          } else if (this.$router) {
            this.$router.push({ path: redirect });
          } else {
            publisher.publish('logged-in');
          }
        } else {
          console.error('No user data available');
          this.reportLoginFailure();
        }
      } catch (error) {
        console.error('Authentication error:', error);
        this.reportLoginFailure();
      }
    }
  },
  components: {
  },
};
</script>
<style scoped>
.login-page {
  min-height: 100vh;
}

.keycloak-login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem;
}

.login-card {
  background: white;
  border-radius: 12px;
  padding: 2.5rem 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  text-align: center;
  min-width: 380px;
  max-width: 440px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

/* Logo Section */
.logo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.5rem;
}

.logo-container {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #191d3b;
  border-radius: 10px;
  flex-shrink: 0;
}

.groove-logo-icon {
  width: 32px;
  height: 32px;
}

/* Header */
.login-header {
  margin-bottom: 1.25rem;
}

.login-header h2 {
  color: #495057;
  font-size: 1rem !important;
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
}

/* Body */
.login-body {
  position: relative;
}

.status-text {
  color: #6c757d;
  font-size: 0.813rem;
  margin-top: 0.875rem;
  margin-bottom: 0;
  font-weight: 400;
}

.spinner-container {
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #FD6481;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Clear Cache Button Styles */
.clear-cache-container {
  margin-top: 2rem;
  padding-top: 1.75rem;
  border-top: 1px solid #e9ecef;
}

.stuck-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  color: #FD6481;
  font-size: 0.813rem;
  margin-bottom: 1rem;
  font-weight: 500;
}

.warning-icon {
  width: 16px;
  height: 16px;
  stroke: #FD6481;
}

.clear-cache-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #FD6481;
  color: white;
  border: none;
  padding: 0.688rem 1.375rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(253, 100, 129, 0.2);
}

.clear-cache-btn:hover {
  background: #fc507a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(253, 100, 129, 0.3);
}

.clear-cache-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(253, 100, 129, 0.2);
}

.btn-icon {
  width: 16px;
  height: 16px;
  stroke: currentColor;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 640px) {
  .login-card {
    min-width: 100%;
    padding: 2rem 1.5rem;
  }

  .keycloak-login-container {
    padding: 1rem;
  }

  .login-header h2 {
    font-size: 0.875rem !important;
  }

  .clear-cache-btn {
    font-size: 0.813rem;
    padding: 0.625rem 1.125rem;
  }
}
</style>
